import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import { ActionLink } from "@/components/shared/action-buttons"
import { useIndexProps } from "./page-props"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import { educatorsTeacherTeamInvitation } from "@/routes"
import { BadgeCheck, Ban, ExternalLink, Inbox, UserSearch } from "lucide-react"
import { useTranslation } from "react-i18next"
import { PageBody } from "@/components/ui/layout"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { Badge } from "@/components/ui/badge"
import { Link, usePathParam } from "@/lib/page-rendering"

export default function Page() {
  const { t } = useTranslation(["invitations", "common"])
  const { invitations, team_name } = useIndexProps()
  const teacherId = usePathParam("teacherId")
  const teamId = usePathParam("teamId")

  return (
    <>
      <EducatorPageChrome
        records={{ "educators/teams/edit": team_name }}
        action={
          <ActionLink
            variant="solid"
            href="/search/educators?listing_type.keyword=personal"
            Icon={UserSearch}
          >
            {t("index.find")}
          </ActionLink>
        }
      />
      <PageBody role="region">
        <Card data-testid="invitation-list">
          <CardHeader>
            <CardTitle>{team_name}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-6 py-6">
            {invitations.length == 0 ? (
              <Empty className="col-span-3">
                <EmptyHeader>
                  <EmptyMedia
                    variant="icon"
                    className="text-muted size-20 bg-white"
                  >
                    <Inbox className="size-16" />
                  </EmptyMedia>
                  <EmptyTitle>{t("index.empty_list_title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("index.empty_list_description")}
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <div className="flex flex-row gap-6">
                    <Link
                      href="/search/educators?listing_type.keyword=team"
                      as={Button}
                    >
                      <UserSearch />
                      {t("index.find")}
                    </Link>
                  </div>
                </EmptyContent>
              </Empty>
            ) : (
              invitations.map((invitation) => (
                <Card className="w-full" data-testid="invitation-item">
                  <CardHeader className="flex flex-row items-center justify-between align-bottom">
                    <CardTitle data-testid="teacher-name">
                      {invitation.teacher_name}
                    </CardTitle>
                    <Badge>
                      {t(`invitations:index.status.${invitation.state}`)}
                    </Badge>
                  </CardHeader>
                  <CardFooter className="flex flex-row flex-wrap gap-4">
                    <a
                      href={invitation.teacher_profile_url}
                      target="_blank"
                      className={buttonVariants()}
                    >
                      <ExternalLink />
                      {t("index.profile")}
                    </a>
                    {invitation.under_consideration && (
                      <ConfirmationDialog
                        title={t("invitations:index.actions.allow_acceptance")}
                        description={t(
                          "invitations:index.actions.allow_acceptance_description",
                        )}
                        action={
                          <Link
                            href={
                              educatorsTeacherTeamInvitation({
                                teacherId,
                                teamId,
                                id: invitation.id,
                              }).url
                            }
                            method="patch"
                            as={Button}
                            data={{
                              status: "ready_to_accept",
                            }}
                          >
                            {t("invitations:index.actions.allow_acceptance")}
                          </Link>
                        }
                      >
                        <ConfirmationDialogTrigger asChild>
                          <Button>
                            <BadgeCheck />
                            {t("invitations:index.actions.allow_acceptance")}
                          </Button>
                        </ConfirmationDialogTrigger>
                      </ConfirmationDialog>
                    )}
                    <ConfirmationDialog
                      title={t("invitations:index.actions.cancel")}
                      description={t(
                        "invitations:index.actions.cancel_description",
                      )}
                      action={
                        <Link
                          className={buttonVariants({ variant: "destructive" })}
                          href={
                            educatorsTeacherTeamInvitation({
                              teacherId,
                              teamId,
                              id: invitation.id,
                            }).url
                          }
                          method="patch"
                          as={Button}
                          data={{
                            status: "cancelled",
                          }}
                        >
                          {t("common:actions.cancel")}
                        </Link>
                      }
                    >
                      <ConfirmationDialogTrigger asChild>
                        <Button variant={"destructive-outline"}>
                          <Ban />
                          {t("common:actions.cancel")}
                        </Button>
                      </ConfirmationDialogTrigger>
                    </ConfirmationDialog>
                  </CardFooter>
                </Card>
              ))
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}
